// src/theme/ui.js
// Componentes primitivos reutilizables del rediseño. Todos leen el tema con
// useTheme() → funcionan en dark (tablet) y light (escritorio) sin cambios.
// Usan estilos inline (mismo patrón que el resto de la app) para migrar fácil.

import React from "react";
import { useTheme } from "./ThemeProvider";

// ── Screen: contenedor pantalla-completa (modales de carga/entrega, etc.) ──
export function Screen({ children, style }) {
  const t = useTheme();
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: t.color.bg, color: t.color.text,
      display: "flex", flexDirection: "column", ...style,
    }}>
      {children}
    </div>
  );
}

// ── AppHeader: título + subtítulo/progreso + acción a la derecha ──
export function AppHeader({ title, subtitle, right, progress, icon }) {
  const t = useTheme();
  const pct = typeof progress === "number" ? Math.max(0, Math.min(1, progress)) : null;
  return (
    <div style={{
      padding: `${t.space.md}px ${t.space.lg}px`,
      borderBottom: `1px solid ${t.color.border}`,
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.space.md,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: t.space.sm }}>
          {icon ? <span style={{ fontSize: t.font.xl }}>{icon}</span> : null}
          <span style={{ fontSize: t.font.xl, fontWeight: t.weight.regular, color: t.color.text }}>{title}</span>
        </div>
        {subtitle != null && (
          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: t.space.sm }}>
            {pct != null && (
              <div style={{ width: 120, height: 6, borderRadius: t.radius.pill, background: t.color.border, overflow: "hidden" }}>
                <div style={{ width: `${Math.round(pct * 100)}%`, height: "100%", background: t.color.success }} />
              </div>
            )}
            <span style={{ fontSize: t.font.sm, color: t.color.textMuted }}>{subtitle}</span>
          </div>
        )}
      </div>
      {right != null && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

// ── Card: superficie. tone: "default" | "success" | "accent" | "danger" ──
export function Card({ children, onClick, tone = "default", style }) {
  const t = useTheme();
  const tones = {
    default: { bg: t.color.surface, bd: t.color.border },
    success: { bg: t.color.successBg, bd: t.color.successBorder },
    accent:  { bg: t.color.accentBg, bd: t.color.accent },
    danger:  { bg: t.color.dangerBg, bd: t.color.dangerBorder },
  };
  const c = tones[tone] || tones.default;
  return (
    <div onClick={onClick} style={{
      background: c.bg, border: `1px solid ${c.bd}`,
      borderRadius: t.radius.lg, padding: `${t.space.lg}px ${t.space.lg + 2}px`,
      boxShadow: t.shadow.card, cursor: onClick ? "pointer" : "default",
      display: "flex", alignItems: "center", gap: t.space.lg, ...style,
    }}>
      {children}
    </div>
  );
}

// ── Button: variant primary|success|secondary|ghost|danger, size sm|md|lg ──
export function Button({ children, onClick, variant = "secondary", size = "md", icon, full, disabled, style }) {
  const t = useTheme();
  const pads = { sm: `${t.space.sm - 2}px ${t.space.md}px`, md: `${t.space.sm + 1}px ${t.space.lg - 2}px`, lg: `${t.space.md + 1}px ${t.space.xl + 4}px` };
  const fonts = { sm: t.font.sm, md: t.font.base, lg: t.font.lg };
  const variants = {
    primary:   { bg: t.color.accent, fg: t.color.accentText, bd: "transparent" },
    success:   { bg: t.color.success, fg: t.color.successText, bd: "transparent" },
    secondary: { bg: t.color.surfaceAlt, fg: t.color.text, bd: t.color.border },
    ghost:     { bg: "transparent", fg: t.color.textMuted, bd: t.color.border },
    danger:    { bg: t.color.dangerBg, fg: t.color.dangerSoft, bd: t.color.dangerBorder },
  };
  const v = variants[variant] || variants.secondary;
  const isOff = disabled;
  return (
    <button onClick={onClick} disabled={isOff} style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: t.space.sm,
      padding: pads[size], borderRadius: t.radius.md,
      border: `1px solid ${isOff ? t.color.border : v.bd}`,
      background: isOff ? t.color.surfaceAlt : v.bg,
      color: isOff ? t.color.textFaint : v.fg,
      fontSize: fonts[size], fontWeight: t.weight.regular,
      cursor: isOff ? "not-allowed" : "pointer",
      width: full ? "100%" : undefined, whiteSpace: "nowrap", ...style,
    }}>
      {icon ? <span style={{ fontSize: fonts[size] }}>{icon}</span> : null}
      {children}
    </button>
  );
}

// ── Chip: etiqueta pill. tone: neutral|accent|success|danger|warning ──
export function Chip({ children, tone = "neutral" }) {
  const t = useTheme();
  const tones = {
    neutral: { bg: t.color.chipBg, fg: t.color.chipText },
    accent:  { bg: t.color.accentBg, fg: t.color.accentOn },
    success: { bg: t.color.successBg, fg: t.color.successSoft },
    danger:  { bg: t.color.dangerBg, fg: t.color.dangerSoft },
    warning: { bg: t.color.warningBg, fg: t.color.warningSoft },
  };
  const c = tones[tone] || tones.neutral;
  return (
    <span style={{
      fontSize: t.font.sm, fontWeight: t.weight.regular, color: c.fg, background: c.bg,
      padding: "2px 9px", borderRadius: t.radius.pill, whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

// ── ProgressBar independiente ──
export function ProgressBar({ value = 0, width = "100%" }) {
  const t = useTheme();
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div style={{ width, height: 6, borderRadius: t.radius.pill, background: t.color.border, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: t.color.success }} />
    </div>
  );
}

// ── SegmentedToggle: selector de 2+ opciones [{id,label,icon}] ──
export function SegmentedToggle({ options, value, onChange }) {
  const t = useTheme();
  return (
    <div style={{ display: "flex", background: t.color.surface, borderRadius: t.radius.md, padding: 4, gap: 4 }}>
      {options.map(o => {
        const active = o.id === value;
        return (
          <button key={o.id} onClick={() => onChange(o.id)} style={{
            flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
            padding: `${t.space.sm + 1}px`, borderRadius: t.radius.md - 3, border: "none",
            background: active ? t.color.borderStrong : "transparent",
            color: active ? t.color.text : t.color.textMuted,
            fontSize: t.font.md, fontWeight: t.weight.regular, cursor: "pointer",
          }}>
            {o.icon ? <span>{o.icon}</span> : null}{o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── CheckCircle: indicador circular. state: done|empty|partial ──
export function CheckCircle({ state = "empty", label, size = 44, emptyIcon = "" }) {
  const t = useTheme();
  const common = {
    width: size, height: size, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  };
  if (state === "done") {
    return <div style={{ ...common, background: t.color.success, color: "#fff", fontSize: 24, fontWeight: t.weight.black }}>✓</div>;
  }
  if (state === "partial") {
    return <div style={{ ...common, border: `2px solid ${t.color.accent}`, color: t.color.accentOn, fontSize: t.font.md, fontWeight: t.weight.regular }}>{label || ""}</div>;
  }
  return <div style={{ ...common, border: `2px solid ${t.color.borderStrong}`, color: t.color.textFaint, fontSize: 22 }}>{emptyIcon}</div>;
}

// ── BottomBar: barra inferior sticky (resumen izq + acción der) ──
export function BottomBar({ children, style }) {
  const t = useTheme();
  return (
    <div style={{
      marginTop: "auto", padding: `${t.space.md + 2}px ${t.space.lg}px`,
      borderTop: `1px solid ${t.color.border}`, background: t.color.surfaceAlt,
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.space.md, ...style,
    }}>
      {children}
    </div>
  );
}
